import { BarChartOutlined, DownloadOutlined, InfoCircleOutlined, LineChartOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Button, Radio, Select, Tooltip, Typography } from 'antd'
import { useAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { UserAvatar } from '@/components/user-name.component'
import { ProjectDetailTable } from '@/components/user-resource-dashboard/detail-table/project-detail-table'
import { UserDetailTable } from '@/components/user-resource-dashboard/detail-table/user-detail-table'
import { DashboardLineChart } from '@/components/user-resource-dashboard/user-resource-dashboard-chart/dashboard-chart-line'
import { departmentsMapAtom } from '@/modules/department'
import { usersAtom } from '@/modules/user'
import { Team } from '@/schemas'

export const TeamTrendDashboardCard: React.FC<{
  type: 'project' | 'user'
  departmentId?: number
  team: Team
}> = ({ type, departmentId, team }) => {
  const { t } = useTranslation()
  const [users] = useAtom(usersAtom)
  const [departmentsMap] = useAtom(departmentsMapAtom)

  const [selected, setSelected] = React.useState()
  const [chartType, setChartType] = React.useState<'line' | 'interval'>('line')
  const [data, setData] = React.useState({ chart: [], list: [] })
  const [list, setList] = React.useState<any[]>([])
  const [selectOptions, setSelectOptions] = React.useState<any[]>([])

  const startDate = new URLSearchParams(window.location.search).get('startDate')
  const endDate = new URLSearchParams(window.location.search).get('endDate')

  const { data: trendProjectsData, run: runTrendProjectsData } = useRequest(
    (selfIds?: string[]) => ({
      url: '/api/work-resource/statistics/team/trend/projects/data',
      params: {
        startDate,
        endDate,
        departmentId,
        selfIds,
      },
    }),
    {
      manual: true,
      initialData: { chart: [], list: [] },
    }
  )

  const { data: trendProjectsList, run: runTrendProjectsList } = useRequest(
    {
      url: '/api/work-resource/statistics/team/trend/projects/list',
      params: {
        startDate,
        endDate,
        departmentId,
      },
    },
    {
      manual: true,
      initialData: [],
    }
  )

  const { data: trendPeopleData, run: runTrendPeopleData } = useRequest(
    (email?: string) => ({
      url: '/api/work-resource/statistics/team/trend/people/data',
      params: {
        startDate,
        endDate,
        departmentId,
        emails: [email],
      },
    }),
    {
      manual: true,
      initialData: { chart: [], list: [] },
    }
  )

  const { data: trendPeopleList, run: runTrendPeopleList } = useRequest(
    {
      url: '/api/work-resource/statistics/team/trend/people/list',
      params: {
        startDate,
        endDate,
        departmentId,
      },
    },
    {
      manual: true,
      initialData: [],
    }
  )

  const { run: runDownloadExcel } = useRequest(
    {
      url: '/api/work-resource/statistics/team/trend/people/excel',
      method: 'POST',
      data: {
        startDate,
        endDate,
        departmentId,
      },
      responseType: 'blob',
    },
    {
      manual: true,
      onSuccess: (res: any) => {
        saveAs(res, `团队资源明细-${team.name}-${startDate}-${endDate}.xlsx`)
      },
    }
  )

  React.useEffect(() => {
    if (startDate && endDate && departmentId) {
      if (type === 'project') {
        runTrendProjectsList()
      } else {
        runTrendPeopleList()
      }
    }
  }, [startDate, endDate, departmentId, type])

  React.useEffect(() => {
    if (type === 'project') {
      setList(trendProjectsList as any)
    } else {
      setList(trendPeopleList as any)
    }
  }, [trendProjectsList, trendPeopleList, type])

  React.useEffect(() => {
    if (type === 'project') {
      setSelectOptions(
        list!.map((p) => (
          <Select.Option key={p.selfId} value={p.selfId} name={p.name}>
            <span>{p.name}</span>
          </Select.Option>
        ))
      )
      setSelected(list!.slice(0, 6).map((l) => ({ value: l.selfId, name: l.name })) as any)
    } else {
      setSelectOptions(
        list!.map((u: any) => {
          const user = users.find((user) => user.email === u.email)
          // if (!user) return
          const email = user?.email || u.email
          const name = user?.name || u.email
          return (
            <Select.Option key={email} value={email} user={user} name={email}>
              <div className='flex'>
                <UserAvatar className='mr-8px' size='small' email={email} />
                <div className='text-xs leading-tight'>
                  <div>{name}</div>
                  <Typography.Text type='secondary'>
                    {user?.departmentIds
                      .map((id) => departmentsMap![id]?.name)
                      .filter(Boolean)
                      .join(',')}
                  </Typography.Text>
                </div>
              </div>
            </Select.Option>
          )
        })
      )
      list[0] ? setSelected({ value: list[0].email, name: list[0].email } as any) : setSelected(undefined)
    }
  }, [list, type])

  React.useEffect(() => {
    if (type === 'project') {
      setData(trendProjectsData as any)
    } else {
      setData(trendPeopleData as any)
    }
  }, [trendProjectsData, trendPeopleData, type])

  React.useEffect(() => {
    if (startDate && endDate && departmentId) {
      if (type === 'project') {
        // fetch new Data selected => []
        selected && runTrendProjectsData((selected as any[]).map((s) => s.value))
      } else if (type === 'user') {
        // fetch new Data selected => {}
        selected ? runTrendPeopleData(selected['value']) : runTrendPeopleData(undefined)
      }
    }
  }, [selected, type])

  return (
    <div className='flex mb-8'>
      <div className='flex-grow mr-16'>
        <div className='flex mb-4 justify-between'>
          <div className='flex font-medium text-lg gap-1 items-baseline'>
            {t(`${type === 'project' ? '项目' : '人员'}视角`)}
            <Tooltip
              title={
                type === 'project'
                  ? '默认展示时间范围内团队和其下子团队资源消耗做多的6个项目，“项目”指的是三大类项目：客户项目、产品项目和研发项目'
                  : '默认展示时间范围内团队和其下子团队普通项目最多的人，项目分摊比例已纳入计算'
              }
            >
              <InfoCircleOutlined className='cursor-pointer text-sm' size={14} />
            </Tooltip>
          </div>
          <div className='flex'>
            <Select
              className='mr-4 w-60'
              labelInValue
              mode={type === 'project' ? 'multiple' : undefined}
              maxTagCount={1}
              value={selected}
              onChange={(v) => setSelected(v)}
              showSearch
              showArrow
              optionFilterProp='name'
            >
              {selectOptions}
            </Select>
            <Radio.Group onChange={(e) => setChartType(e.target.value)} value={chartType}>
              <Radio.Button value='line'>
                <LineChartOutlined />
              </Radio.Button>
              <Radio.Button value='interval'>
                <BarChartOutlined />
              </Radio.Button>
            </Radio.Group>
          </div>
        </div>
        <DashboardLineChart
          data={data!.chart.map((c: any) => ({
            ...c,
            date: t('dashboard.xAxisLabel', { year: c.year, week: c.week }),
          }))}
          type={chartType}
        />
      </div>
      <div className='flex-shrink-0 w-90'>
        <div className='flex font-medium text-lg mb-4 gap-1 items-baseline justify-between'>
          <span className='flex gap-1 items-baseline'>
            {t(`${type === 'project' ? '项目' : '人员'}资源明细`)}
            <Tooltip
              title={
                type === 'project'
                  ? '项目团队工时/团队总工时，分摊比例已纳入计算，显示TOP3资源占用团队'
                  : '该项目的人员工时/人员总工时，项目分摊比例已纳入计算'
              }
            >
              <InfoCircleOutlined className='cursor-pointer text-sm' size={14} />
            </Tooltip>
          </span>
          {type === 'user' && (
            <Tooltip title={t('下载')}>
              <Button shape='circle' icon={<DownloadOutlined />} onClick={() => runDownloadExcel()} />
            </Tooltip>
          )}
        </div>
        {type === 'project' ? (
          <ProjectDetailTable list={data!.list as any} type='team' />
        ) : (
          <UserDetailTable list={data!.list as any} />
        )}
      </div>
    </div>
  )
}
